import {
  collection,
  doc,
  getDoc,
  getDocs,
  getDocFromServer,
} from "firebase/firestore";
import firestoreInstance from "../services/firestoreInstance";
import { Participation, ParticipationFirestore } from "../types";

// 🔥 Fonction générique : charge les participations, players et privateData
export async function fetchParticipationsWithPlayers(
  forceRefresh: boolean = false
): Promise<Participation[]> {
  const db = firestoreInstance;
  // Charger les participations
  const participationSnap = await getDocs(collection(db, "participation"));
  const privateQueue: { ref: any; target: any }[] = [];
  const playerIdSet = new Set<string>();

  const participationList = participationSnap.docs
    .map((docSnap) => {
      const data = docSnap.data() as ParticipationFirestore;

      // Vérifier que les données essentielles existent
      if (!data || !data.playersId || !data.dateInscription) {
        console.warn(
          `Participation ${docSnap.id} a des données manquantes:`,
          data
        );
        return null;
      }

      const participation = {
        id: docSnap.id,
        ...data,
        dateInscription: data.dateInscription.toDate(),
      } as Participation;

      privateQueue.push({
        ref: doc(
          firestoreInstance,
          "participation",
          docSnap.id,
          "private",
          "data"
        ),
        target: participation,
      });
      playerIdSet.add(data.playersId);
      return participation;
    })
    .filter((p): p is Participation => p !== null); // Type guard pour filtrer les null

  // Charger les privateData
  const BATCH_SIZE = 100;
  for (let i = 0; i < privateQueue.length; i += BATCH_SIZE) {
    const batch = privateQueue.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async ({ ref, target }) => {
        try {
          const snap = await getDoc(ref);
          type PrivateData = {
            email?: string;
            paymentStatus?: string;
          };

          const privateData = snap.exists() ? (snap.data() as PrivateData) : {};
          target.email = privateData.email ?? null;
          target.paymentStatus = privateData.paymentStatus ?? null;
        } catch (error) {
          console.warn(
            `Erreur lors du chargement des données privées pour ${target.id}:`,
            error
          );
          target.email = null;
          target.paymentStatus = null;
        }
      })
    );
  }

  // Charger les players
  const playersMap = new Map();
  const playerIds = Array.from(playerIdSet);
  for (let i = 0; i < playerIds.length; i += BATCH_SIZE) {
    const batch = playerIds.slice(i, i + BATCH_SIZE);
    const snaps = await Promise.all(
      batch.map(async (id) => {
        try {
          // Forcer le rechargement depuis le serveur si demandé
          if (forceRefresh) {
            return await getDocFromServer(doc(db, "players", id));
          } else {
            return await getDoc(doc(db, "players", id));
          }
        } catch (error) {
          console.warn(`Erreur lors du chargement du joueur ${id}:`, error);
          return null;
        }
      })
    );
    for (const snap of snaps) {
      if (snap && snap.exists()) {
        const playerData = snap.data();
        if (playerData && playerData.name && playerData.surname) {
          playersMap.set(snap.id, { ...playerData, id: snap.id });
        } else {
          console.warn(
            `Joueur ${snap.id} a des données incomplètes:`,
            playerData
          );
        }
      }
    }
  }

  // Associer players
  const finalParticipations = participationList
    .map((p) => {
      const player = playersMap.get(p.playersId);
      if (!player) {
        console.warn(
          `Joueur non trouvé pour la participation ${p.id}: ${p.playersId}`
        );
        return null;
      }
      return {
        ...p,
        players: player,
      };
    })
    .filter((p): p is Participation => p !== null); // Type guard pour filtrer les null

  return finalParticipations;
}
